import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { HumanCheck, useHumanCheck } from "@/components/HumanCheck";

function formatDetail(d) {
  if (!d) return "Login failed";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(d);
}

export default function AdminLoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const human = useHumanCheck();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password, human.fields);
      toast.success("Signed in");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatDetail(human.errorText(err.response?.data?.detail)) || err.message);
      human.refresh();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 sm:px-6 py-16" data-testid="admin-login-page">
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-slate-900 text-white"><Lock size={16} /></div>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Admin sign in</h1>
            <p className="text-xs text-slate-500">Manage facility status and records</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
          </div>
          <HumanCheck check={human} idPrefix="admin-login" />
          <Button type="submit" data-testid="admin-login-submit" disabled={submitting} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
            {submitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
